import { URL_PARAM } from '../../defaults';
import type { ResolvedColumn } from '../schema/column';
import type { FilterOperator, FilterType, ResolvedFilter } from './filter';
import type { SortState } from './sort';

export type SortDirection = 'ascending' | 'descending';

export interface FilterParamInput {
    key: string;
    operator: FilterOperator;
    value: string | string[];
    valueTo?: string;
}

export interface SortParamInput {
    key: string;
    direction: SortDirection;
}

const ENTRY_SEPARATOR = ',';
const PART_SEPARATOR = ':';
const VALUE_SEPARATOR = '|';

const OPERATORS = new Set<FilterOperator>([
    'contains',
    'equals',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
    'oneOf',
    'between',
]);

const TOKEN_BY_DIR: Record<SortState['dir'], SortDirection> = {
    asc: 'ascending',
    desc: 'descending',
};

const DIR_BY_TOKEN: Record<string, SortState['dir'] | undefined> = {
    ascending: 'asc',
    descending: 'desc',
};

export function filterParamName(tableId: string): string {
    return `${URL_PARAM.filter}${tableId}`;
}

export function lockedFilterParamName(tableId: string): string {
    return `${URL_PARAM.lockedFilter}${tableId}`;
}

export function sortParamName(tableId: string): string {
    return `${URL_PARAM.sort}${tableId}`;
}

export function searchParamName(tableId: string): string {
    return `${URL_PARAM.search}${tableId}`;
}

function safeDecode(value: string): string | null {
    try {
        return decodeURIComponent(value);
    } catch {
        return null;
    }
}

function encodeEntry(key: string, token: string, values: string[]): string {
    const value = values.map(encodeURIComponent).join(VALUE_SEPARATOR);
    return [encodeURIComponent(key), token, value].join(PART_SEPARATOR);
}

function valuesOf(filter: ResolvedFilter): string[] {
    const raw = Array.isArray(filter.value)
        ? filter.value
        : [
              filter.value instanceof Date
                  ? filter.value.toISOString()
                  : String(filter.value),
          ];
    if (filter.valueTo === undefined) return raw;
    const to =
        filter.valueTo instanceof Date
            ? filter.valueTo.toISOString()
            : String(filter.valueTo);
    return [...raw, to];
}

export function serializeFilters(filters: ResolvedFilter[]): string {
    return filters
        .flatMap((filter) => {
            if (filter.operator === 'between' && filter.valueTo === undefined)
                return [];
            if (
                filter.operator === 'oneOf' &&
                Array.isArray(filter.value) &&
                filter.value.length === 0
            ) {
                return [];
            }
            return encodeEntry(filter.key, filter.operator, valuesOf(filter));
        })
        .join(ENTRY_SEPARATOR);
}

interface FilterTarget {
    type: FilterType;
}

function filterTargets<T>(
    columns: ResolvedColumn<T>[],
): Map<string, FilterTarget> {
    const targets = new Map<string, FilterTarget>();
    columns.forEach((col) => {
        if (!col.canFilter || !col.filter) return;
        targets.set(col.filterKey ?? col.id, { type: col.filter });
    });
    return targets;
}

function sortKeys<T>(columns: ResolvedColumn<T>[]): Set<string> {
    const keys = new Set<string>();
    columns.forEach((col) => {
        if (col.canSort) keys.add(col.sortKey ?? col.id);
    });
    return keys;
}

function warnDroppedUrlState(kind: 'filter' | 'sort', raw: string): void {
    if (process.env.NODE_ENV === 'production') return;
    // eslint-disable-next-line no-console -- intentional dev-only diagnostic, guarded by NODE_ENV check
    console.warn(
        `[ics-data-table] Dropped invalid ${kind} URL state: "${raw}"`,
    );
}

function serializeFilterParamInputs(
    fnName: string,
    filters: FilterParamInput[],
): string {
    return filters
        .flatMap((f) => {
            if (f.operator === 'between' && f.valueTo === undefined) {
                throw new Error(
                    `${fnName}: "between" filter on "${f.key}" needs valueTo.`,
                );
            }
            if (
                f.operator === 'oneOf' &&
                Array.isArray(f.value) &&
                f.value.length === 0
            ) {
                return [];
            }
            const values = Array.isArray(f.value) ? f.value : [f.value];
            return encodeEntry(
                f.key,
                f.operator,
                f.valueTo === undefined ? values : [...values, f.valueTo],
            );
        })
        .join(ENTRY_SEPARATOR);
}

function parseEntry(
    entry: string,
    targets: Map<string, FilterTarget>,
): ResolvedFilter | null {
    const parts = entry.split(PART_SEPARATOR);
    if (parts.length !== 3) {
        warnDroppedUrlState('filter', entry);
        return null;
    }

    const key = safeDecode(parts[0]);
    if (key === null) {
        warnDroppedUrlState('filter', entry);
        return null;
    }
    const target = targets.get(key);
    if (!target) {
        warnDroppedUrlState('filter', entry);
        return null;
    }

    const token = parts[1];
    if (!OPERATORS.has(token as FilterOperator)) {
        warnDroppedUrlState('filter', entry);
        return null;
    }
    const operator = token as FilterOperator;

    const decoded = parts[2].split(VALUE_SEPARATOR).map(safeDecode);
    if (decoded.some((v) => v === null)) {
        warnDroppedUrlState('filter', entry);
        return null;
    }
    const values = decoded as string[];
    if (operator === 'oneOf')
        return { key, type: target.type, operator, value: values };
    if (operator === 'between') {
        if (values.length !== 2) {
            warnDroppedUrlState('filter', entry);
            return null;
        }
        return {
            key,
            type: target.type,
            operator,
            value: values[0],
            valueTo: values[1],
        };
    }
    return { key, type: target.type, operator, value: values[0] };
}

export function parseFilters<T>(
    raw: string,
    columns: ResolvedColumn<T>[],
): ResolvedFilter[] {
    if (!raw) return [];
    const targets = filterTargets(columns);
    return raw
        .split(ENTRY_SEPARATOR)
        .flatMap((entry) => parseEntry(entry, targets) ?? []);
}

export function serializeSort(sort: SortState): string {
    return `${encodeURIComponent(sort.key)}${PART_SEPARATOR}${TOKEN_BY_DIR[sort.dir]}`;
}

export function parseSort<T>(
    raw: string,
    columns: ResolvedColumn<T>[],
): SortState | null {
    if (!raw) return null;
    const parts = raw.split(PART_SEPARATOR);
    if (parts.length !== 2) {
        warnDroppedUrlState('sort', raw);
        return null;
    }

    const key = safeDecode(parts[0]);
    if (key === null) {
        warnDroppedUrlState('sort', raw);
        return null;
    }
    if (!sortKeys(columns).has(key)) {
        warnDroppedUrlState('sort', raw);
        return null;
    }

    const dir = DIR_BY_TOKEN[parts[1]];
    if (!dir) {
        warnDroppedUrlState('sort', raw);
        return null;
    }
    return { key, dir };
}

export function buildTableFilterParams(
    tableId: string,
    filters: FilterParamInput[],
    sort?: SortParamInput,
    search?: string,
): URLSearchParams {
    const params = new URLSearchParams();
    if (filters.length > 0) {
        const raw = serializeFilterParamInputs(
            'buildTableFilterParams',
            filters,
        );
        if (raw) params.set(filterParamName(tableId), raw);
    }
    if (sort) {
        params.set(
            sortParamName(tableId),
            `${encodeURIComponent(sort.key)}${PART_SEPARATOR}${sort.direction}`,
        );
    }
    if (search) params.set(searchParamName(tableId), search);
    return params;
}

export function buildTableLockedFilterParams(
    tableId: string,
    filters: FilterParamInput[],
): URLSearchParams {
    const params = new URLSearchParams();
    const raw = serializeFilterParamInputs(
        'buildTableLockedFilterParams',
        filters,
    );
    if (raw) params.set(lockedFilterParamName(tableId), raw);
    return params;
}
